import { svgIconDataUri } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { ImageUpIcon, RefreshCwIcon, Trash2Icon } from "lucide-react";
import { type DragEvent, useRef, useState } from "react";

import { useSvgFilePick } from "../lib/useSvgFilePick";

/**
 * Drop-or-browse upload for one SVG logo (the Openers page's opener logo). The file is checked
 * here (`useSvgFilePick`) for instant feedback and again by the mutation, which is the
 * authority. The preview is an `<img>` data URI, the path every opener takes; inlining the
 * markup instead would turn tenant input into XSS (see api-contract's svgIcon.ts). A checkerboard
 * sits behind it so a transparent logo reads as one.
 */
export function SvgLogoDropzone({
  id,
  value,
  onChange,
  maxBytes,
  help,
  disabled = false,
}: {
  id: string;
  /** SVG source; "" = none. */
  value: string;
  onChange: (svg: string) => void;
  maxBytes: number;
  help: string;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const { pick, error, clearError } = useSvgFilePick({ maxBytes, onPick: onChange });

  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled) void pick(e.dataTransfer.files[0]);
  };

  return (
    <div className="grid gap-2">
      <input
        ref={input}
        id={id}
        type="file"
        accept=".svg,image/svg+xml"
        className="peer sr-only"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Clear the input so re-picking the same file fires onChange again.
          e.target.value = "";
          void pick(file);
        }}
      />
      <label
        htmlFor={id}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-dragging={dragging || undefined}
        className="group relative flex min-h-28 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-input bg-muted/30 p-4 transition-colors hover:border-primary/60 hover:bg-primary/5 peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-disabled:cursor-not-allowed peer-disabled:opacity-60 data-dragging:border-primary data-dragging:bg-primary/10"
      >
        {value ? (
          <div
            className="flex h-20 w-full max-w-64 items-center justify-center rounded-lg p-2"
            style={{
              backgroundImage:
                "conic-gradient(var(--muted) 25%, transparent 0 50%, var(--muted) 0 75%, transparent 0)",
              backgroundSize: "14px 14px",
            }}
          >
            <img
              src={svgIconDataUri(value)}
              alt="Uploaded logo"
              className="max-h-full max-w-full object-contain transition-transform group-hover:scale-105"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-center">
            <span className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform group-hover:-translate-y-0.5 group-data-dragging:scale-110">
              <ImageUpIcon className="size-5" />
            </span>
            <span className="text-sm font-medium">
              {dragging ? "Drop to upload" : "Drop an SVG here, or click to browse"}
            </span>
          </div>
        )}
        {value && dragging && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 text-sm font-medium backdrop-blur-sm">
            Drop to replace
          </div>
        )}
      </label>
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
        {error ? (
          <p className="min-w-48 flex-1 text-xs text-destructive">{error}</p>
        ) : (
          <p className="min-w-48 flex-1 text-xs text-muted-foreground">{help}</p>
        )}
        {value && (
          <div className="flex shrink-0 gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => input.current?.click()}
            >
              <RefreshCwIcon />
              Replace
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => {
                clearError();
                onChange("");
              }}
            >
              <Trash2Icon />
              Remove
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
